import { useEffect, useRef } from 'react';
import { fetchEvaluationRun } from '../api/evaluationRunApi';
import { finishEvaluationProgress, inFlightEvaluationRuns } from '../utils/finishEvaluationProgress';

/**
 * Keeps an evaluation page current across WebSocket gaps. On each realtime
 * resync it reloads the evaluation in the background and looks up, by id,
 * every run still in flight locally, including `pendingRun` (the
 * recalculation the page started): the reloaded evaluation shows the newest
 * run that did not fail, so a run that failed during the gap is not in it.
 * The progress entries of a run that finished get its final status, and the
 * pending run gets an entry if every update for it was missed. The progress
 * entries of the displayed run also end when the evaluation reports it
 * finished.
 */
export function useEvaluationResync({
    clientId,
    usecaseId,
    realtimeResyncTick,
    reloadInBackground,
    evaluation,
    evaluationProgress,
    setEvaluationProgress,
    pendingRun = null,
}) {
    const latest = useRef(null);
    latest.current = { reloadInBackground, evaluationProgress, pendingRun };

    useEffect(() => {
        if (!realtimeResyncTick || !usecaseId) return undefined;
        const { reloadInBackground: reload, evaluationProgress: progress, pendingRun: pending } = latest.current;
        reload();
        let cancelled = false;
        for (const run of inFlightEvaluationRuns(progress, usecaseId, pending)) {
            fetchEvaluationRun({ clientId, usecaseId, ...run }).then((result) => {
                if (cancelled) return;
                const addMissing = latest.current.pendingRun?.runId === run.runId;
                setEvaluationProgress?.((prev) => finishEvaluationProgress(prev, usecaseId, result, { addMissing }));
            });
        }
        return () => {
            cancelled = true;
        };
    }, [realtimeResyncTick]);

    // A recalculation whose start answers after a resync was not looked up by it, and every update for it may
    // have been missed: look it up once the page knows its run.
    useEffect(() => {
        const run = pendingRun;
        if (!realtimeResyncTick || !usecaseId || !run?.runId) return undefined;
        let cancelled = false;
        fetchEvaluationRun({ clientId, usecaseId, ...run }).then((result) => {
            if (cancelled) return;
            setEvaluationProgress?.((prev) => finishEvaluationProgress(prev, usecaseId, result, { addMissing: true }));
        });
        return () => {
            cancelled = true;
        };
    }, [pendingRun?.runId]);

    useEffect(() => {
        setEvaluationProgress?.((prev) => finishEvaluationProgress(prev, usecaseId, evaluation));
    }, [evaluation, usecaseId, setEvaluationProgress]);
}
